/**
 * LocaleSwitcher — minimal EN/FR toggle for public pages.
 *
 * Behaviour:
 *  - Click flips `i18n.language`, persists to localStorage, and rewrites
 *    the URL prefix (/foo → /fr/foo, /fr/foo → /foo) so the locale is
 *    shareable and survives a refresh.
 *  - Hidden when only one locale is registered (defensive — supports
 *    future locale toggles by config alone).
 */
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { SUPPORTED_LOCALES, persistLocale } from "@/i18n";
import { Globe } from "lucide-react";

const CODES = SUPPORTED_LOCALES.map((l) => l.code);

function rewritePath(pathname, target) {
  // Strip any existing locale prefix and re-add the target one (en is implicit).
  const segments = (pathname || "/").split("/").filter(Boolean);
  if (segments.length && CODES.includes(segments[0])) {
    segments.shift();
  }
  const tail = segments.join("/");
  if (target === "en") {
    return `/${tail}`;
  }
  return tail ? `/${target}/${tail}` : `/${target}`;
}

export function LocaleSwitcher({ tone = "dark" }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const current = (i18n.language || "en").slice(0, 2);

  if (SUPPORTED_LOCALES.length <= 1) return null;

  const handle = (code) => {
    if (code === current) return;
    i18n.changeLanguage(code);
    persistLocale(code);
    const next = rewritePath(location.pathname, code) + (location.search || "");
    navigate(next, { replace: true });
  };

  const baseClass =
    tone === "dark"
      ? "border-white/15 text-white/70 hover:text-white"
      : "border-black/10 text-ek-ink/70 hover:text-ek-ink";

  return (
    <div
      className={`inline-flex items-center rounded-full border ${baseClass} text-[11px] font-semibold`}
      data-testid="locale-switcher"
    >
      <Globe size={12} className="mx-1.5 opacity-60" aria-hidden="true" />
      {SUPPORTED_LOCALES.map((loc) => {
        const active = loc.code === current;
        return (
          <button
            key={loc.code}
            type="button"
            data-testid={`locale-switcher-${loc.code}`}
            aria-pressed={active}
            onClick={() => handle(loc.code)}
            className={`rounded-full px-2 py-0.5 transition-colors ${
              active
                ? "bg-ek-purple text-white"
                : "hover:bg-white/10"
            }`}
          >
            {loc.short}
          </button>
        );
      })}
    </div>
  );
}

export default LocaleSwitcher;
